import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, CheckCircle, Stack } from "@phosphor-icons/react";

/**
 * CompleteVesselModal — captures the final Total Tons Loaded when a supervisor
 * marks a vessel as completed. Pre-populates with the latest TOB metric so the
 * supervisor only confirms (or adjusts) the figure.
 */
export default function CompleteVesselModal({ vessel, latestTob = 0, tonnage = 0, onClose, onCompleted }) {
    const [tons, setTons] = useState(Math.round(latestTob || 0).toString());
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);

    const parsed = parseFloat(tons);
    const valid = !Number.isNaN(parsed) && parsed >= 0;
    const overTonnage = tonnage && parsed > tonnage * 1.05;

    const submit = async (e) => {
        e.preventDefault();
        if (!valid) {
            toast.error("Enter the total tons loaded");
            return;
        }
        if (overTonnage && !window.confirm(`${parsed.toLocaleString()} t exceeds vessel tonnage (${tonnage.toLocaleString()} t). Continue?`)) {
            return;
        }
        setLoading(true);
        try {
            await api.post(`/vessels/${vessel.id}/complete`, {
                total_tons_loaded: parsed,
                note: note || null,
            });
            toast.success("Vessel marked completed");
            onCompleted && onCompleted();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="complete-modal">
            <div className="g-card w-full max-w-md overflow-hidden">
                <div className="g-card-header-dark" style={{ background: "var(--grindrod-green)" }}>
                    <CheckCircle size={18} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide flex-1">Mark vessel completed</div>
                    <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="complete-close">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div className="rounded-sm bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600">
                        <div className="font-semibold text-[var(--grindrod-navy)] uppercase tracking-wide text-[10px]">{vessel.vessel_name}</div>
                        <div>Vessel tonnage: <span className="tabular font-semibold">{Number(tonnage || 0).toLocaleString()} t</span></div>
                        <div>Latest TOB on file: <span className="tabular font-semibold">{Number(latestTob || 0).toLocaleString()} t</span></div>
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1.5">
                            <Stack size={12} className="inline mr-1" /> Total tons loaded <span className="text-[var(--grindrod-red)]">*</span>
                        </label>
                        <input
                            type="number"
                            min="0"
                            step="any"
                            value={tons}
                            onChange={(e) => setTons(e.target.value)}
                            className="g-input tabular text-2xl font-bold"
                            autoFocus
                            required
                            data-testid="complete-tons-input"
                        />
                        <div className="text-[11px] text-slate-500 mt-1.5">
                            This is the final figure that will appear on the Performance Certificate, History, and Analytics.
                        </div>
                        {overTonnage && (
                            <div className="text-[11px] text-[var(--grindrod-red)] mt-1 font-semibold" data-testid="complete-warning">
                                Warning: above vessel tonnage of {Number(tonnage).toLocaleString()} t.
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1.5">Note <span className="text-slate-400 normal-case lowercase tracking-normal">(optional)</span></label>
                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. Confirmed by draft survey"
                            className="g-input"
                            data-testid="complete-note"
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline" data-testid="complete-cancel">Cancel</button>
                        <button type="submit" className="btn-primary" disabled={loading || !valid} data-testid="complete-submit" style={{ background: "var(--grindrod-green)" }}>
                            {loading ? "Completing…" : "Confirm & complete"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
